import { createURL } from '@sm/utils';
import { t } from '@sm/intl';
import COPY from './getFooterCopy';

import { getHelpLink } from '../../../..';
import {
  showBoardOfDirectorsLink,
  showNewsRoom,
  showDachPaymentLinks,
  showImprintLink,
  showBlog,
} from '../../../../helpers/languageHelpers';

export const getSlimLinks = lang => {
  return [
    {
      title: t(COPY.ABOUT_SURVEYMONKEY),
      url: createURL('/mp/aboutus/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.PRIVACY_POLICY),
      url: createURL('/mp/legal/privacy-policy/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.CALIFORNIA_PRIVACY_POLICY),
      url: createURL('/mp/legal/region-specific-privacy-statement/', {
        ut_source: 'footer',
      }),
      anchorAttributes: { rel: 'nofollow' },
    },
    lang === 'de' && {
      title: 'Impressum',
      url: createURL('/mp/impressum/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.EMAIL_OPT_IN),
      url: createURL('/user/email-opt-in/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.OFFICE_LOCATIONS),
      url: createURL('/mp/aboutus/office-locations/', { ut_source: 'footer' }),
      lastItem: true,
    },
  ].filter(Boolean);
};

export const getCommunityLinks = (domain, lang) => {
  return [
    {
      title: t(COPY.COMMUNITY),
      url: 'https://www.facebook.com/surveymonkey/',
      isTitle: true,
    },
    showBlog(lang) && {
      title: t(COPY.OUR_BLOG),
      url: createURL('/curiosity/', { ut_source: 'footer' }),
    },
    {
      title: 'Facebook',
      url: 'https://www.facebook.com/surveymonkey/',
    },
    {
      title: 'Twitter',
      url: 'https://twitter.com/SurveyMonkey',
    },
    {
      title: 'LinkedIn',
      url: 'https://www.linkedin.com/company/surveymonkey/',
    },
    {
      title: 'Instagram',
      url: 'https://www.instagram.com/surveymonkey/',
    },
    {
      title: 'YouTube',
      url: 'https://www.youtube.com/surveymonkey',
    },
    {
      title: t(COPY.DEVELOPERS),
      url: createURL(`https://developer.${domain}.com`, {
        ut_source: 'footer',
      }),
      lastItem: true,
    },
  ].filter(Boolean);
};

export const getAboutUsLinks = (domain, lang, country) => {
  return [
    {
      title: t(COPY.ABOUT_US),
      url: createURL('/mp/aboutus/', { ut_source: 'footer' }),
      isTitle: true,
    },
    {
      title: t(COPY.LEADERSHIP_TEAM),
      url: createURL('/mp/aboutus/leadership/', { ut_source: 'footer' }),
    },
    showBoardOfDirectorsLink(lang) && {
      title: t(COPY.BOARD_OF_DIRECTORS),
      url: createURL('/mp/aboutus/directors/', { ut_source: 'footer' }),
    },
    showNewsRoom(lang, country) && {
      title: t(COPY.INVESTOR_RELATIONS),
      url: 'https://investor.surveymonkey.com/',
    },
    {
      title: t(COPY.APP_DIRECTORY),
      url: createURL('/apps/', { ut_source: 'footer' }),
    },
    showNewsRoom(lang, country) && {
      title: t(COPY.NEWSROOM),
      url: createURL('/newsroom/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.OFFICE_LOCATIONS),
      url: createURL('/mp/aboutus/office-locations/', { ut_source: 'footer' }),
    },
    showDachPaymentLinks(lang, country) && {
      title: 'Impressum',
      url: createURL('/mp/impressum/', { ut_source: 'footer' }),
    },
    showDachPaymentLinks(lang, country) && {
      title: t(COPY.PAYMENT_METHODS),
      url: getHelpLink(lang, {
        helpName: 'Payment-Methods',
        params: { ut_source: 'footer' },
      }),
    },
    showImprintLink(lang, country) && {
      title: t(COPY.IMPRINT),
      url: createURL(`www.${domain}/mp/imprint`, { ut_source: 'footer' }),
    },
    {
      title: t(COPY.CAREERS),
      url: createURL('/careers/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.SITEMAP),
      url: createURL('/mp/sitemap/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.HELP),
      url: getHelpLink(lang, {
        params: { ut_source: 'footer' },
      }),
      lastItem: true,
    },
  ].filter(Boolean);
};

export const getPolicesLink = lang => {
  return [
    {
      title: t(COPY.POLICIES),
      url: createURL('/mp/legal/', { ut_source: 'footer' }),
      isTitle: true,
    },
    {
      title: t(COPY.TERMS_OF_USE),
      url: createURL('/mp/legal/terms-of-use/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.PRIVACY_POLICY),
      url: createURL('/mp/legal/privacy-policy/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.CALIFORNIA_PRIVACY_POLICY),
      url: createURL('/mp/legal/region-specific-privacy-statement/', {
        ut_source: 'footer',
      }),
      anchorAttributes: { rel: 'nofollow' },
    },
    {
      title: t(COPY.ANTI_SPAM_POLICY),
      url: createURL('/mp/legal/anti-spam-policy/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.SECURITY_STATEMENT),
      url: createURL('/mp/legal/security/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.GDPR_COMPLIANCE),
      url: createURL('/mp/privacy/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.EMAIL_OPT_IN),
      url: createURL('/user/email-opt-in/', { ut_source: 'footer' }),
    },
    {
      title: t(COPY.ACCESSIBILITY),
      url: getHelpLink(lang, {
        helpName: '508-Compliance',
        params: { ut_source: 'footer' },
      }),
    },
    {
      title: t(COPY.COOKIES_POLICY),
      url: createURL('/mp/legal/cookies/', { ut_source: 'footer' }),
      lastItem: true,
    },
  ];
};

export const getAnonCategories = (domain, lang, country, variant) => {
  const defaultLinks = [
    getCommunityLinks(domain, lang),
    getAboutUsLinks(domain, lang, country),
    getPolicesLink(lang),
  ];
  const slimLinks = [getSlimLinks(lang)];
  return variant === 'default' ? defaultLinks : slimLinks;
};
